import { useState, useEffect, useCallback, useRef, useMemo } from "react";

// ── API 키
const KMA_API_KEY = "ac247916bd22e6e7e4fb8e93ab8d81c6e14b37c02d7280cad29e175c3085b26c";
const TELEGRAM_BOT_TOKEN = "8947209107:AAF_X29lk0fil5UXthVPdBW0DKzp0zYyn4o";

async function sendTelegramAlert(message, recipients) {
  if (!TELEGRAM_BOT_TOKEN || TELEGRAM_BOT_TOKEN === "YOUR_TELEGRAM_BOT_TOKEN") {
    console.log("[텔레그램 시뮬레이션]", message);
    return recipients.map(r => ({ ok: true, simulated: true, name: r.name }));
  }
  return Promise.all(recipients.map(async (r) => {
    try {
      const res = await fetch(`https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendMessage`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chat_id: r.chatId, text: message, parse_mode: "HTML" }),
      });
      const data = await res.json();
      return { ...data, name: r.name };
    } catch (e) { return { ok: false, error: e.message, name: r.name }; }
  }));
}

async function fetchTelegramUpdates() {
  if (!TELEGRAM_BOT_TOKEN || TELEGRAM_BOT_TOKEN === "YOUR_TELEGRAM_BOT_TOKEN") return [];
  try {
    const res = await fetch(`https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/getUpdates?limit=100`);
    const data = await res.json();
    if (!data.ok) return [];
    const seen = new Map();
    data.result.forEach(u => {
      const user = u.message?.from || u.callback_query?.from;
      const chatId = u.message?.chat?.id || u.callback_query?.message?.chat?.id;
      if (user && chatId && !seen.has(chatId)) {
        seen.set(chatId, { chatId: String(chatId), name: [user.first_name, user.last_name].filter(Boolean).join(" ") || user.username || String(chatId), username: user.username || "" });
      }
    });
    return Array.from(seen.values());
  } catch { return []; }
}

const HEAT_LEVELS = {
  관심: { min:31, max:33, color:"#22C55E", emoji:"🟩", label:"관심 (폭염주의보)", tempRange:"31°C 이상", actions:["근로자 폭염정보 제공","시원한 물, 그늘(휴식공간), 바람 준비","쿨토시 등 보냉장구 제공","고강도 작업자와 민감군 관리"], telegramAlert:false },
  주의: { min:33, max:35, color:"#EAB308", emoji:"🟨", label:"주의 (폭염주의보)", tempRange:"33°C 이상", actions:["매시간 10분씩 휴식 제공","무더위 시간대 옥외작업 단축 또는 작업시간대 조정"], telegramAlert:true },
  경고: { min:35, max:38, color:"#F97316", emoji:"🟧", label:"경고 (폭염경보)",   tempRange:"35°C 이상", actions:["매시간 15분씩 휴식 제공","무더위 시간대 불가피한 경우 제외하고 옥외작업 중지"], telegramAlert:true },
  위험: { min:38, max:999, color:"#EF4444", emoji:"🟥", label:"위험 (폭염경보)",  tempRange:"38°C 이상", actions:["매시간 15분씩 휴식 제공","무더위 시간대 재난·안전관리 작업 외 옥외작업 중지"], telegramAlert:true },
};
const NORMAL_LEVEL = { color:"#7BA5D8", emoji:"⬜", label:"정상", tempRange:"31°C 미만", actions:["정상 작업 가능","수시로 수분 보충"], telegramAlert:false };
const getLvl = (lv) => HEAT_LEVELS[lv] || NORMAL_LEVEL;
const wxIcon = (f) => f>=38?"🔥":f>=35?"☀️":f>=33?"🌤️":f>=31?"⛅":"🌥️";

const RN320_DEVICE_EUI = "ac1f09fffe1a4812"; // 신평택센터(id 28) 실제 구매 게이트웨이
const LOGISTICS_CENTERS = [
  { id:1,  name:"강릉물류센터",    lat:37.6768, lon:128.8958, nx:93, ny:130, workers:0, zone:"강원" },
  { id:2,  name:"원주물류센터",    lat:37.2981, lon:127.8712, nx:75, ny:121, workers:0, zone:"강원" },
  { id:3,  name:"발안물류센터",    lat:37.1895, lon:126.8450, nx:58, ny:119, workers:0, zone:"경기" },
  { id:4,  name:"남사물류센터",    lat:37.1580, lon:127.1990, nx:64, ny:118, workers:0, zone:"경기" },
  { id:5,  name:"신오산물류센터",  lat:37.1498, lon:127.0523, nx:61, ny:118, workers:0, zone:"경기" },
  { id:28, name:"신평택물류센터",  lat:36.9850, lon:126.8350, nx:57, ny:114, workers:0, zone:"경기" },
  { id:6,  name:"화성물류센터",    lat:37.1640, lon:126.9870, nx:60, ny:118, workers:0, zone:"경기" },
  { id:7,  name:"용인물류센터",    lat:37.2820, lon:127.2150, nx:64, ny:121, workers:0, zone:"경기" },
  { id:8,  name:"고양물류센터",    lat:37.6780, lon:126.8140, nx:57, ny:129, workers:0, zone:"경기" },
  { id:9,  name:"포천물류센터",    lat:37.8940, lon:127.2000, nx:64, ny:134, workers:0, zone:"경기" },
  { id:10, name:"송파물류센터",    lat:37.5042, lon:127.1144, nx:62, ny:125, workers:0, zone:"서울" },
  { id:11, name:"곤지암물류센터",  lat:37.3640, lon:127.3710, nx:67, ny:122, workers:0, zone:"경기" },
  { id:12, name:"신남양주물류센터",lat:37.6290, lon:127.3380, nx:66, ny:128, workers:0, zone:"경기" },
  { id:13, name:"고촌물류센터",    lat:37.6190, lon:126.7280, nx:56, ny:128, workers:0, zone:"경기" },
  { id:14, name:"김포물류센터",    lat:37.6190, lon:126.7280, nx:56, ny:128, workers:0, zone:"경기" },
  { id:15, name:"인천물류센터",    lat:37.4600, lon:126.6580, nx:54, ny:124, workers:0, zone:"인천" },
  { id:16, name:"경산물류센터",    lat:35.8720, lon:128.7810, nx:92, ny:91,  workers:0, zone:"경북" },
  { id:17, name:"김해물류센터",    lat:35.2890, lon:128.8320, nx:93, ny:78,  workers:0, zone:"경남" },
  { id:18, name:"양산물류센터",    lat:35.3480, lon:129.0190, nx:97, ny:79,  workers:0, zone:"경남" },
  { id:19, name:"진주물류센터",    lat:35.1540, lon:128.1130, nx:81, ny:75,  workers:0, zone:"경남" },
  { id:20, name:"칠곡물류센터",    lat:36.1120, lon:128.4350, nx:86, ny:96,  workers:0, zone:"경북" },
  { id:21, name:"부산물류센터",    lat:35.1780, lon:128.9740, nx:96, ny:76,  workers:0, zone:"부산" },
  { id:22, name:"신제주물류센터",  lat:33.4620, lon:126.3780, nx:50, ny:37,  workers:0, zone:"제주" },
  { id:23, name:"공주물류센터",    lat:36.3200, lon:126.9000, nx:62, ny:102, workers:0, zone:"충남" },
  { id:24, name:"청주물류센터",    lat:36.7000, lon:127.6500, nx:67, ny:105, workers:0, zone:"충북" },
  { id:25, name:"익산물류센터",    lat:35.9370, lon:127.0460, nx:61, ny:91,  workers:0, zone:"전북" },
  { id:26, name:"장성물류센터",    lat:35.5000, lon:126.5000, nx:56, ny:77,  workers:0, zone:"전남" },
  { id:27, name:"신광주물류센터",  lat:35.0500, lon:126.6500, nx:57, ny:75,  workers:0, zone:"광주" },
];

function calcFeelsLike(temp, humidity, windSpeed) {
  if (temp >= 27) {
    const T=temp, R=humidity;
    return Math.round((-8.78469475556+1.61139411*T+2.338548839*R-0.14611605*T*R-0.012308094*T*T-0.016424828*R*R+0.002211732*T*T*R+0.00072546*T*R*R-0.000003582*T*T*R*R)*10)/10;
  }
  if (temp<=10 && windSpeed>1.3) return Math.round((13.12+0.6215*temp-11.37*Math.pow(windSpeed*3.6,0.16)+0.3965*temp*Math.pow(windSpeed*3.6,0.16))*10)/10;
  return temp;
}
function getHeatLevel(f) { return f>=38?"위험":f>=35?"경고":f>=33?"주의":f>=31?"관심":"정상"; }

async function fetchKMAWeather(nx, ny) {
  const now=new Date();
  // KST = UTC+9
  const kst=new Date(now.getTime()+9*60*60*1000);
  const baseDate=kst.toISOString().slice(0,10).replace(/-/g,"");
  const hour=kst.getUTCHours(), minute=kst.getUTCMinutes();
  const baseTime=minute<40?`${String(hour-1).padStart(2,"0")}00`:`${String(hour).padStart(2,"0")}00`;
  const url=`https://apis.data.go.kr/1360000/VilageFcstInfoService_2.0/getUltraSrtNcst?serviceKey=${KMA_API_KEY}&numOfRows=10&pageNo=1&dataType=JSON&base_date=${baseDate}&base_time=${baseTime}&nx=${nx}&ny=${ny}`;
  try {
    const res=await fetch(url);
    const data=await res.json();
    const items=data.response?.body?.items?.item||[];
    const r={};
    items.forEach(item=>{ r[item.category]=parseFloat(item.obsrValue); });
    return { temp:r["T1H"]??null, humidity:r["REH"]??null, windSpeed:r["WSD"]??null, rain:r["RN1"]??0, precipType:r["PTY"]??0 };
  } catch {
    const baseTemp=31+Math.random()*11;
    return { temp:Math.round(baseTemp*10)/10, humidity:Math.round(60+Math.random()*30), windSpeed:Math.round((0.5+Math.random()*3)*10)/10, rain:0, precipType:0, simulated:true };
  }
}

function getTargetRecipients(recipients, centerId) {
  return recipients.filter(r => {
    if (!r.active) return false;
    if (r.role==="전체관리자") return true;
    if (r.role==="센터관리자") return Array.isArray(r.assignedCenters)&&r.assignedCenters.includes(centerId);
    return false;
  });
}

const API_BASE = "/api";

// ── 실제 시도 경계 좌표 (카카오맵 Polygon용) ──────────────────────────────
// 주요 꼭짓점으로 실제 행정구역 경계를 최대한 반영
const REAL_ZONE_PATHS = {
  서울: [[37.691,126.764],[37.715,126.830],[37.702,127.004],[37.703,127.124],[37.683,127.183],[37.573,127.183],[37.428,127.094],[37.428,126.900],[37.491,126.820],[37.545,126.764]],
  인천: [[37.809,126.200],[37.809,126.780],[37.600,126.780],[37.600,126.520],[37.380,126.520],[37.250,126.340],[37.100,126.200]],
  경기: [[38.300,126.800],[38.300,127.900],[37.900,127.900],[37.900,127.500],[37.700,127.400],[37.560,127.270],[37.430,127.100],[37.150,127.100],[37.000,126.900],[37.000,126.520],[37.300,126.650],[37.580,126.780],[37.720,126.820],[38.000,126.820]],
  강원: [[38.620,127.050],[38.620,129.400],[37.300,129.400],[36.900,129.000],[36.900,128.200],[37.200,127.900],[37.400,127.700],[37.700,127.700],[37.900,127.500],[37.900,127.050]],
  충남: [[37.050,126.050],[37.050,127.100],[36.800,127.100],[36.500,127.000],[36.000,127.100],[35.900,126.900],[35.900,126.200],[36.300,125.800],[36.700,125.800]],
  충북: [[37.200,127.100],[37.200,128.200],[36.800,128.400],[36.400,128.400],[36.100,128.100],[36.000,127.800],[36.300,127.100],[36.800,127.100]],
  전북: [[36.000,126.600],[36.100,127.800],[35.800,127.800],[35.700,127.600],[35.500,127.200],[35.500,126.700],[35.700,126.400],[36.000,126.400]],
  전남: [[35.500,126.200],[35.500,127.600],[35.200,127.800],[34.800,127.700],[34.300,126.900],[34.200,126.200],[34.500,125.800],[35.200,125.800]],
  광주: [[35.280,126.700],[35.280,126.980],[35.050,126.980],[35.050,126.700]],
  경북: [[37.200,127.900],[37.200,129.600],[36.700,129.600],[36.200,129.400],[35.600,129.200],[35.600,128.500],[35.800,128.100],[36.100,127.900],[36.500,127.900]],
  경남: [[35.600,127.600],[35.600,128.500],[35.800,128.800],[35.600,129.200],[35.100,129.400],[34.600,128.600],[34.600,127.800],[35.000,127.600]],
  부산: [[35.420,128.750],[35.420,129.310],[35.000,129.310],[35.040,128.750]],
  대구: [[36.100,128.300],[36.100,128.820],[35.750,128.820],[35.700,128.450],[35.800,128.300]],
  대전: [[36.600,127.200],[36.600,127.640],[36.200,127.640],[36.200,127.200]],
  울산: [[35.650,129.000],[35.650,129.500],[35.380,129.500],[35.380,129.000]],
  세종: [[36.700,127.100],[36.700,127.400],[36.400,127.400],[36.400,127.100]],
  제주: [[33.580,126.100],[33.580,127.000],[33.100,127.000],[33.100,126.100]],
};

// 시도명 레이블 위치
const ZONE_LABEL = {
  서울:{lat:37.563,lon:126.960}, 인천:{lat:37.456,lon:126.605},
  경기:{lat:37.450,lon:127.000}, 강원:{lat:37.700,lon:128.400},
  충남:{lat:36.500,lon:126.700}, 충북:{lat:36.700,lon:127.900},
  전북:{lat:35.750,lon:127.100}, 전남:{lat:34.800,lon:126.800},
  광주:{lat:35.160,lon:126.840}, 경북:{lat:36.500,lon:128.800},
  경남:{lat:35.200,lon:128.100}, 부산:{lat:35.180,lon:129.050},
  대구:{lat:35.870,lon:128.580}, 대전:{lat:36.370,lon:127.390},
  울산:{lat:35.550,lon:129.250}, 세종:{lat:36.490,lon:127.280},
  제주:{lat:33.370,lon:126.540},
};

function LoginScreen({ onLogin }) {
  const [tab, setTab] = useState("login");
  const [employeeId, setEmployeeId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [org, setOrg] = useState("");
  const [signupEmployeeId, setSignupEmployeeId] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("전체관리자");
  const [assignedCenter, setAssignedCenter] = useState("");
  const [signupMsg, setSignupMsg] = useState("");

  const doLogin = async () => {
    setError(""); setLoading(true);
    try {
      const res = await fetch("/api/auth/login", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ employeeId, password }) });
      const data = await res.json();
      if (!data.ok) { setError(data.error||"로그인 실패"); setLoading(false); return; }
      onLogin({ token:data.token, mustChangePassword:data.mustChangePassword, name:data.name, role:data.role, employeeId:data.employeeId, assignedCenters:data.assignedCenters });
    } catch (e) { setError("서버 오류가 발생했습니다."); }
    setLoading(false);
  };

  const doSignup = async () => {
    setSignupMsg(""); setLoading(true);
    try {
      const res = await fetch("/api/auth/signup", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({
        org, employeeId: signupEmployeeId, name, phone, role,
        assignedCenters: role==="센터관리자" ? [Number(assignedCenter)] : [],
      })});
      const data = await res.json();
      if (!data.ok) { setSignupMsg(data.error||"신청 실패"); setLoading(false); return; }
      setSignupMsg("✅ 가입 신청이 접수되었습니다. 관리자 승인 후 텔레그램으로 안내드립니다.");
    } catch (e) { setSignupMsg("서버 오류가 발생했습니다."); }
    setLoading(false);
  };

  return (
    <div style={{fontFamily:"'Noto Sans KR',sans-serif",background:"#0F1F3D",minHeight:"100vh",color:"#F0F6FF",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{width:360,background:"linear-gradient(160deg,#162B50,#091525)",border:"1px solid #274878",borderRadius:16,padding:24}}>
        <div style={{fontSize:18,fontWeight:800,marginBottom:4,textAlign:"center"}}>🌡️ 온열질환 대시보드</div>
        <div style={{fontSize:11,color:"#7BA5D8",textAlign:"center",marginBottom:18}}>GS리테일 물류센터</div>
        <div style={{display:"flex",gap:2,marginBottom:16,background:"#0B1830",borderRadius:8,padding:3}}>
          <div onClick={()=>setTab("login")} style={{flex:1,textAlign:"center",padding:8,borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:700,background:tab==="login"?"#4DA6FF":"transparent",color:tab==="login"?"#fff":"#7BA5D8"}}>로그인</div>
          <div onClick={()=>setTab("signup")} style={{flex:1,textAlign:"center",padding:8,borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:700,background:tab==="signup"?"#4DA6FF":"transparent",color:tab==="signup"?"#fff":"#7BA5D8"}}>회원가입</div>
        </div>

        {tab==="login" ? (
          <>
            <input value={employeeId} onChange={e=>setEmployeeId(e.target.value)} placeholder="사번(아이디)" style={{width:"100%",background:"#0B1830",border:"1px solid #274878",borderRadius:8,padding:10,fontSize:13,color:"#E8F4FF",marginBottom:8,boxSizing:"border-box"}}/>
            <input value={password} onChange={e=>setPassword(e.target.value)} type="password" placeholder="비밀번호" style={{width:"100%",background:"#0B1830",border:"1px solid #274878",borderRadius:8,padding:10,fontSize:13,color:"#E8F4FF",marginBottom:12,boxSizing:"border-box"}}/>
            {error && <div style={{color:"#EF4444",fontSize:11,marginBottom:10}}>{error}</div>}
            <div onClick={doLogin} style={{background:"#4DA6FF",color:"#fff",textAlign:"center",fontSize:13,fontWeight:700,padding:11,borderRadius:8,cursor:"pointer",opacity:loading?0.6:1}}>{loading?"확인 중...":"로그인"}</div>
          </>
        ) : (
          <>
            <input value={org} onChange={e=>setOrg(e.target.value)} placeholder="소속" style={{width:"100%",background:"#0B1830",border:"1px solid #274878",borderRadius:8,padding:9,fontSize:12,color:"#E8F4FF",marginBottom:6,boxSizing:"border-box"}}/>
            <input value={signupEmployeeId} onChange={e=>setSignupEmployeeId(e.target.value)} placeholder="사번" style={{width:"100%",background:"#0B1830",border:"1px solid #274878",borderRadius:8,padding:9,fontSize:12,color:"#E8F4FF",marginBottom:6,boxSizing:"border-box"}}/>
            <input value={name} onChange={e=>setName(e.target.value)} placeholder="이름" style={{width:"100%",background:"#0B1830",border:"1px solid #274878",borderRadius:8,padding:9,fontSize:12,color:"#E8F4FF",marginBottom:6,boxSizing:"border-box"}}/>
            <input value={phone} onChange={e=>setPhone(e.target.value)} placeholder="연락처" style={{width:"100%",background:"#0B1830",border:"1px solid #274878",borderRadius:8,padding:9,fontSize:12,color:"#E8F4FF",marginBottom:10,boxSizing:"border-box"}}/>

            <div style={{fontSize:11,color:"#7BA5D8",fontWeight:700,marginBottom:5}}>역할 선택</div>
            {[
              {v:"전체관리자",d:"전국 27개 물류센터 전체 모니터링 및 경보 알림 수신"},
              {v:"센터관리자",d:"담당 센터만 모니터링 및 경보 알림 수신"},
            ].map(r=>(
              <div key={r.v} onClick={()=>setRole(r.v)} style={{border:role===r.v?"1.5px solid #4DA6FF":"1px solid #274878",background:role===r.v?"#4DA6FF14":"transparent",borderRadius:8,padding:8,marginBottom:5,cursor:"pointer"}}>
                <div style={{fontSize:12,fontWeight:700,color:role===r.v?"#4DA6FF":"#7BA5D8"}}>{role===r.v?"◉":"○"} {r.v}</div>
                <div style={{fontSize:10,color:"#7A9AC0",marginTop:2}}>{r.d}</div>
              </div>
            ))}

            {role==="센터관리자" && (
              <select value={assignedCenter} onChange={e=>setAssignedCenter(e.target.value)} style={{width:"100%",background:"#0B1830",border:"1.5px solid #4DA6FF",borderRadius:8,padding:9,fontSize:12,color:"#E8F4FF",marginBottom:10,boxSizing:"border-box"}}>
                <option value="">담당 센터 선택</option>
                {LOGISTICS_CENTERS.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}

            <div style={{background:"#0B1830",border:"1px solid #1E5A8A",borderRadius:8,padding:9,marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:700,color:"#4DA6FF",marginBottom:4}}>📱 텔레그램 알림 받는 법 (가입 전 필수)</div>
              <div style={{fontSize:10,color:"#7A9AC0",lineHeight:1.7}}>1. 텔레그램 앱 설치<br/>2. @t67_jungseok_bot 검색<br/>3. "시작(Start)" 버튼 클릭<br/>4. 위에 입력한 사번을 그대로 메시지로 전송</div>
            </div>

            {signupMsg && <div style={{color:signupMsg.startsWith("✅")?"#4ADEAA":"#EF4444",fontSize:11,marginBottom:10}}>{signupMsg}</div>}
            <div onClick={doSignup} style={{background:"#4DA6FF",color:"#fff",textAlign:"center",fontSize:13,fontWeight:700,padding:11,borderRadius:8,cursor:"pointer",opacity:loading?0.6:1}}>{loading?"처리 중...":"가입 신청"}</div>
          </>
        )}
      </div>
    </div>
  );
}

function ChangePasswordScreen({ token, onChanged }) {
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setError("");
    if (pw1.length < 6) { setError("비밀번호는 6자 이상이어야 합니다."); return; }
    if (pw1 !== pw2) { setError("비밀번호가 일치하지 않습니다."); return; }
    setLoading(true);
    try {
      const res = await fetch("/api/auth/change-password", { method:"POST", headers:{"Content-Type":"application/json", "Authorization":`Bearer ${token}`}, body: JSON.stringify({ newPassword: pw1 }) });
      const data = await res.json();
      if (!data.ok) { setError(data.error||"변경 실패"); setLoading(false); return; }
      onChanged();
    } catch (e) { setError("서버 오류가 발생했습니다."); }
    setLoading(false);
  };

  return (
    <div style={{fontFamily:"'Noto Sans KR',sans-serif",background:"#0F1F3D",minHeight:"100vh",color:"#F0F6FF",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{width:340,background:"linear-gradient(160deg,#162B50,#091525)",border:"1px solid #274878",borderRadius:16,padding:24}}>
        <div style={{fontSize:15,fontWeight:800,marginBottom:6}}>⚠️ 비밀번호 변경 필요</div>
        <div style={{fontSize:11,color:"#7BA5D8",marginBottom:16}}>최초 로그인입니다. 새 비밀번호를 설정해주세요.</div>
        <input value={pw1} onChange={e=>setPw1(e.target.value)} type="password" placeholder="새 비밀번호 (6자 이상)" style={{width:"100%",background:"#0B1830",border:"1px solid #274878",borderRadius:8,padding:10,fontSize:13,color:"#E8F4FF",marginBottom:8,boxSizing:"border-box"}}/>
        <input value={pw2} onChange={e=>setPw2(e.target.value)} type="password" placeholder="새 비밀번호 확인" style={{width:"100%",background:"#0B1830",border:"1px solid #274878",borderRadius:8,padding:10,fontSize:13,color:"#E8F4FF",marginBottom:12,boxSizing:"border-box"}}/>
        {error && <div style={{color:"#EF4444",fontSize:11,marginBottom:10}}>{error}</div>}
        <div onClick={submit} style={{background:"#4ADEAA",color:"#052015",textAlign:"center",fontSize:13,fontWeight:700,padding:11,borderRadius:8,cursor:"pointer",opacity:loading?0.6:1}}>{loading?"처리 중...":"비밀번호 변경"}</div>
      </div>
    </div>
  );
}

function ApprovalScreen({ token, onClose }) {
  const [pending, setPending] = useState([]);
  const [edits, setEdits] = useState({});

  useEffect(()=>{
    fetch("/api/auth/pending", { headers:{"Authorization":`Bearer ${token}`} }).then(r=>r.json()).then(setPending).catch(()=>{});
  },[token]);

  const approve = async (u) => {
    const e = edits[u.id] || {};
    const role = e.role || u.role;
    const rawCenter = e.assignedCenter || (u.assignedCenters||[])[0] || "";
    const assignedCenters = (role==="센터관리자" && rawCenter) ? [Number(rawCenter)] : [];
    await fetch(`/api/auth/approve/${u.id}`, { method:"POST", headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`}, body: JSON.stringify({ role, assignedCenters }) });
    setPending(pending.filter(p=>p.id!==u.id));
  };
  const reject = async (u) => {
    await fetch(`/api/auth/reject/${u.id}`, { method:"POST", headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`}, body: JSON.stringify({}) });
    setPending(pending.filter(p=>p.id!==u.id));
  };

  return (
    <div style={{fontFamily:"'Noto Sans KR',sans-serif",background:"#0F1F3D",minHeight:"100vh",color:"#F0F6FF",padding:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{fontSize:16,fontWeight:800}}>👤 가입 승인 관리 (대기 {pending.length}건)</div>
        <div onClick={onClose} style={{background:"#1E3F70",border:"1px solid #2E5EA0",color:"#B8D0F0",padding:"7px 14px",borderRadius:8,cursor:"pointer",fontSize:12}}>대시보드로</div>
      </div>
      {pending.length===0 && <div style={{color:"#5A80B0",fontSize:13}}>대기 중인 가입 신청이 없습니다.</div>}
      <div style={{display:"flex",flexDirection:"column",gap:10,maxWidth:400}}>
        {pending.map(u=>{
          const e = edits[u.id] || { role:u.role, assignedCenter:(u.assignedCenters||[])[0]||"" };
          return (
            <div key={u.id} style={{background:"#111827",border:"1px solid #274878",borderRadius:10,padding:12}}>
              <div style={{fontSize:13,fontWeight:700}}>{u.name} · {u.employeeId}</div>
              <div style={{fontSize:11,color:"#7A9AC0",margin:"4px 0 8px"}}>{u.org} · {u.phone}</div>
              <select value={e.role} onChange={ev=>setEdits({...edits,[u.id]:{...e,role:ev.target.value}})} style={{width:"100%",background:"#091525",border:"1px solid #274878",color:"#7BA5D8",fontSize:11,padding:6,borderRadius:6,marginBottom:6,boxSizing:"border-box"}}>
                <option value="전체관리자">전체관리자</option>
                <option value="센터관리자">센터관리자</option>
              </select>
              {e.role==="센터관리자" && (
                <select value={e.assignedCenter} onChange={ev=>setEdits({...edits,[u.id]:{...e,assignedCenter:ev.target.value}})} style={{width:"100%",background:"#091525",border:"1px solid #274878",color:"#7BA5D8",fontSize:11,padding:6,borderRadius:6,marginBottom:8,boxSizing:"border-box"}}>
                  <option value="">담당 센터 선택</option>
                  {LOGISTICS_CENTERS.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
              <div style={{display:"flex",gap:6}}>
                <div onClick={()=>approve(u)} style={{flex:1,background:"#22C55E",color:"#fff",textAlign:"center",fontSize:11,fontWeight:700,padding:7,borderRadius:6,cursor:"pointer"}}>승인</div>
                <div onClick={()=>reject(u)} style={{flex:1,background:"#EF4444",color:"#fff",textAlign:"center",fontSize:11,fontWeight:700,padding:7,borderRadius:6,cursor:"pointer"}}>거절</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function HeatstrokeDashboard() {
  const [centers, setCenters] = useState(LOGISTICS_CENTERS.map(c=>({...c,weather:null,loading:true})));
  const [lastUpdated, setLastUpdated] = useState(null);
  const [alertLog, setAlertLog] = useState([]);
  const [emailReportEmail, setEmailReportEmail] = useState("");
  const [emailReportEnabled, setEmailReportEnabled] = useState(false);
  const [emailReportSaved, setEmailReportSaved] = useState(false);
  const [historyData, setHistoryData] = useState({});
  const [mapDisplayMode, setMapDisplayMode] = useState("feels"); // "feels" | "temp"
  const [authUser, setAuthUser] = useState(() => {
    try { const saved = localStorage.getItem("hs_auth"); return saved ? JSON.parse(saved) : null; } catch { return null; }
  });
  const [showApprovalPanel, setShowApprovalPanel] = useState(false);
  const handleLogin = (userData) => {
    const kstDate = new Date(Date.now()+9*60*60*1000).toISOString().slice(0,10);
    const withDate = {...userData, loginDate: kstDate};
    setAuthUser(withDate);
    try { localStorage.setItem("hs_auth", JSON.stringify(withDate)); } catch {}
  };
  const handlePasswordChanged = () => {
    const updated = {...authUser, mustChangePassword:false};
    setAuthUser(updated);
    try { localStorage.setItem("hs_auth", JSON.stringify(updated)); } catch {}
  };
  const handleLogout = () => {
    setAuthUser(null);
    try { localStorage.removeItem("hs_auth"); } catch {}
  };
  useEffect(()=>{
    const checkMidnight = () => {
      if (!authUser || !authUser.loginDate) return;
      const kstDate = new Date(Date.now()+9*60*60*1000).toISOString().slice(0,10);
      if (authUser.loginDate !== kstDate) handleLogout();
    };
    checkMidnight();
    const i = setInterval(checkMidnight, 60*1000);
    return()=>clearInterval(i);
  },[authUser]);
  useEffect(()=>{
    if (!authUser) return;
    fetch("/api/reports/settings", { headers:{"Authorization":`Bearer ${authUser.token}`} })
      .then(r=>r.json()).then(d=>{ if(d.ok){ setEmailReportEmail(d.email||""); setEmailReportEnabled(!!d.enabled); } })
      .catch(()=>{});
  },[authUser]);
  const saveEmailReportSettings = () => {
    fetch("/api/reports/settings", { method:"POST", headers:{"Content-Type":"application/json","Authorization":`Bearer ${authUser.token}`}, body: JSON.stringify({ email: emailReportEmail, enabled: emailReportEnabled }) })
      .then(r=>r.json()).then(d=>{ if(d.ok){ setEmailReportSaved(true); setTimeout(()=>setEmailReportSaved(false),2000); } });
  };
  const [rn320Latest, setRn320Latest] = useState(null);
  const [rn320History, setRn320History] = useState({});
  const [activeTab, setActiveTab] = useState("dashboard");
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [kakaoMap, setKakaoMap] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(true);
  const [newRecipient, setNewRecipient] = useState({name:"",chatId:"",role:"전체관리자",assignedCenters:[]});
  const [recipientError, setRecipientError] = useState("");
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [mapPopup, setMapPopup] = useState(null);

  // 지도 refs
  const mapRef      = useRef(null);
  const mapWrapRef  = useRef(null);  // 지도 감싸는 div (인출선 SVG 기준)
  const overlaysRef = useRef([]);
  const polygonsRef = useRef([]);
  const markersRef  = useRef([]);
  const historyTimerRef = useRef(null);
  const alertedRef  = useRef(new Set());

  // 인출선용 refs
  const leftChipRefs  = useRef({});   // 왼쪽 칩 div ref
  const rightChipRefs = useRef({});   // 오른쪽 칩 div ref
  const [dotPositions, setDotPositions] = useState({}); // 지도 내 점 픽셀 좌표

  // 센터 분류 (수도권 인출선용)
  const PULL_LEFT_SET  = useMemo(()=>new Set([8,13,14,15,3,6,5]),[]);
  const PULL_RIGHT_SET = useMemo(()=>new Set([9,12,10,7,11,4]),[]);
  const leftCenters  = useMemo(()=>centers.filter(c=>PULL_LEFT_SET.has(c.id)).sort((a,b)=>b.lat-a.lat),[centers,PULL_LEFT_SET]);
  const rightCenters = useMemo(()=>centers.filter(c=>PULL_RIGHT_SET.has(c.id)).sort((a,b)=>b.lat-a.lat),[centers,PULL_RIGHT_SET]);


  // ── 날씨 수집
  const fetchAllWeather = useCallback(async () => {
    // 서버가 이미 5분마다 KMA를 수집하므로, 프론트엔드는 서버의 최신값만 조회 (KMA 중복호출 제거)
    const serverCurrent = await fetch("/api/current").then(r=>r.json()).catch(()=>({}));
    const updated = LOGISTICS_CENTERS.map(center => {
      const w = serverCurrent[center.id] || {temp:null, humidity:null, windSpeed:null, feelsLike:null, level:null};
      const feelsLike = w.feelsLike!=null ? w.feelsLike : null;
      const level = w.level || null;
      return {...center, weather:{...w,feelsLike,level}, loading:false};
    });
    setCenters(updated);
    setLastUpdated(new Date());
    updated.forEach(c => {
      if (!c.weather?.level) return;
      const alertKey=`${c.id}-${c.weather.level}-${new Date().getHours()}`;
      const lvlData=HEAT_LEVELS[c.weather.level];
      const targetRecipients=getTargetRecipients(recipients,c.id);
      if (lvlData?.telegramAlert && !alertedRef.current.has(alertKey)) {
        alertedRef.current.add(alertKey);
        const msg=`🚨 <b>[온열질환 ${c.weather.level}]</b>\n\n📦 ${c.name}\n🌡️ 기온: ${c.weather.temp}°C\n🤒 체감: <b>${c.weather.feelsLike}°C</b>\n💧 습도: ${c.weather.humidity}%\n\n⏰ ${new Date().toLocaleTimeString("ko-KR")}`;
        if (targetRecipients.length>0) sendTelegramAlert(msg,targetRecipients);
        setAlertLog(prev=>[{id:Date.now(),center:c.name,centerId:c.id,level:c.weather.level,feelsLike:c.weather.feelsLike,time:new Date(),msg,recipientCount:targetRecipients.length},...prev.slice(0,49)]);
        addNotification(`${c.name}: ${c.weather.level} (${c.weather.feelsLike}°C)`,c.weather.level);
      }
    });
    const now=new Date();
    const kstNow=new Date(now.getTime()+9*60*60*1000);
    const hourKey=`${kstNow.getUTCFullYear()}-${String(kstNow.getUTCMonth()+1).padStart(2,"0")}-${String(kstNow.getUTCDate()).padStart(2,"0")} ${String(kstNow.getUTCHours()).padStart(2,"0")}:00`;
    setHistoryData(prev=>{
      const next={...prev};
      updated.forEach(c=>{
        if (!c.weather?.temp) return;
        if (!next[c.id]) next[c.id]={};
        if (!next[c.id][hourKey] || !Array.isArray(next[c.id][hourKey].temps)) next[c.id][hourKey]={temps:[],feels:[],humidities:[],count:0};
        next[c.id][hourKey].temps.push(c.weather.temp);
        next[c.id][hourKey].feels.push(c.weather.feelsLike);
        next[c.id][hourKey].humidities.push(c.weather.humidity);
        next[c.id][hourKey].count+=1;
        // 매 정시에 서버에 저장
        const mins=kstNow.getUTCMinutes();
        if (mins>=0 && mins<10) {
          const d=next[c.id][hourKey];
          const avgTemp=Math.round((d.temps.reduce((a,b)=>a+b,0)/d.temps.length)*10)/10;
          const avgFeels=Math.round((d.feels.reduce((a,b)=>a+b,0)/d.feels.length)*10)/10;
          const avgHumidity=Math.round(d.humidities.reduce((a,b)=>a+b,0)/d.humidities.length);
          fetch("/api/history",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({centerId:c.id,hourKey,avgTemp,avgFeels,avgHumidity,count:d.count})}).catch(()=>{});
        }
      });
      return next;
    });
    // 서버에서 이력 로드 (매번 갱신, 서버 값이 최신이므로 우선 적용)
    fetch("/api/history").then(r=>r.json()).then(serverHistory=>{
      setHistoryData(prev=>{
        const merged={...prev};
        Object.entries(serverHistory).forEach(([id,data])=>{
          merged[id]={...(merged[id]||{}),...data};
        });
        return merged;
      });
    }).catch(()=>{});
    return updated;
  },[]);

  const addNotification=(message,level)=>{
    const id=Date.now();
    setNotifications(prev=>[{id,message,level},...prev.slice(0,4)]);
    setTimeout(()=>setNotifications(prev=>prev.filter(n=>n.id!==id)),6000);
  };

  // ── 수신자 API
  const loadRecipients = useCallback(async()=>{
    try {
      setRecipientsLoading(true);
      const res=await fetch(`${API_BASE}/recipients`);
      const data=await res.json();
      setRecipients(Array.isArray(data)?data:[]);
    } catch { setRecipientError("서버 연결 실패."); }
    finally { setRecipientsLoading(false); }
  },[]);

  useEffect(()=>{ loadRecipients(); },[loadRecipients]);

  const addRecipient=async()=>{
    const name=newRecipient.name.trim(), chatId=newRecipient.chatId.trim().replace(/\s/g,"");
    if (!name){setRecipientError("이름을 입력하세요.");return;}
    if (!chatId||!/^-?\d+$/.test(chatId)){setRecipientError("Chat ID는 숫자여야 합니다.");return;}
    if (newRecipient.role==="센터관리자"&&newRecipient.assignedCenters.length===0){setRecipientError("담당 센터를 선택하세요.");return;}
    try {
      const res=await fetch(`${API_BASE}/recipients`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({name,chatId,role:newRecipient.role,assignedCenters:newRecipient.role==="전체관리자"?[]:newRecipient.assignedCenters,active:true})});
      if (res.status===409){setRecipientError("이미 등록된 Chat ID입니다.");return;}
      if (!res.ok){setRecipientError("등록 실패.");return;}
      setNewRecipient({name:"",chatId:"",role:"전체관리자",assignedCenters:[]});
      setRecipientError("");
      await loadRecipients();
    } catch {setRecipientError("서버 연결 실패.");}
  };
  const removeRecipient=async(id)=>{ await fetch(`${API_BASE}/recipients/${id}`,{method:"DELETE"}); await loadRecipients(); };
  const toggleRecipient=async(id,cur)=>{ await fetch(`${API_BASE}/recipients/${id}`,{method:"PATCH",headers:{"Content-Type":"application/json"},body:JSON.stringify({active:!cur})}); await loadRecipients(); };
  const toggleCenterAssign=async(rid,cid)=>{
    const r=recipients.find(r=>r.id===rid); if (!r) return;
    const has=(r.assignedCenters||[]).includes(cid);
    const newC=has?r.assignedCenters.filter(c=>c!==cid):[...(r.assignedCenters||[]),cid];
    await fetch(`${API_BASE}/recipients/${rid}`,{method:"PATCH",headers:{"Content-Type":"application/json"},body:JSON.stringify({assignedCenters:newC})});
    await loadRecipients();
  };
  const autoFetchUsers=async()=>{
    setFetchingUsers(true);
    const users=await fetchTelegramUpdates();
    if (users.length===0){setRecipientError("봇에게 메시지를 보낸 사용자가 없습니다.");}
    else {
      let added=0;
      for (const u of users) {
        if (!recipients.find(r=>r.chatId===u.chatId)){
          await fetch(`${API_BASE}/recipients`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({...u,role:"전체관리자",assignedCenters:[],active:true})});
          added++;
        }
      }
      setRecipientError(added===0?"새로운 사용자가 없습니다.":"");
      await loadRecipients();
    }
    setFetchingUsers(false);
  };
  const sendTestMessage=async(recipient)=>{
    const scope=recipient.role==="전체관리자"?"전국 27개 물류센터 전체":`담당 센터: ${(recipient.assignedCenters||[]).map(id=>LOGISTICS_CENTERS.find(c=>c.id===id)?.name||id).join(", ")}`;
    const msg=`✅ <b>[테스트 알림]</b>\n\n안녕하세요, <b>${recipient.name}</b>님!\n온열질환 모니터링 시스템 알림이 정상 연결되었습니다.\n\n🎯 수신 범위: ${scope}\n⏰ ${new Date().toLocaleString("ko-KR")}`;
    await sendTelegramAlert(msg,[recipient]);
  };

  // ── 카카오맵 스크립트 로드
  useEffect(()=>{
    const script=document.createElement("script");
    script.src="//dapi.kakao.com/v2/maps/sdk.js?appkey=b1045031cacd02ea095013dc6e1f6a3d&autoload=false";
    script.async=true;
    document.head.appendChild(script);
    script.onload=()=>{ window.kakao.maps.load(()=>setMapReady(true)); };
    return()=>{ if(document.head.contains(script)) document.head.removeChild(script); };
  },[]);

  // ── 지도 초기화
  useEffect(()=>{
    if (!mapReady||!mapRef.current) return;
    const {kakao}=window;
    if (!kakao?.maps) return;
    if (kakaoMap) {
      kakaoMap.relayout();
      kakaoMap.setCenter(new kakao.maps.LatLng(36.2,127.5));
      return;
    }
    const map=new kakao.maps.Map(mapRef.current,{
      center:new kakao.maps.LatLng(36.2,127.5),
      level:13,
    });
    setKakaoMap(map);
  },[mapReady,authUser,showApprovalPanel]);

  // ── 시도 폴리곤 + 직접 마커 (지도가 준비되고 데이터가 바뀔 때마다)
  useEffect(()=>{
    if (!kakaoMap||!window.kakao) return;
    const {kakao}=window;

    // 기존 제거
    polygonsRef.current.forEach(p=>{ try{p.setMap(null);}catch{} });
    polygonsRef.current=[];
    overlaysRef.current.forEach(o=>{ try{o.setMap(null);}catch{} });
    overlaysRef.current=[];

    // 시도별 최고 경보
    const LO={위험:4,경고:3,주의:2,관심:1,정상:0};
    const zoneMap={};
    centers.forEach(c=>{
      const lv=c.weather?.level||"정상";
      if (!zoneMap[c.zone]||(LO[lv]||0)>(LO[zoneMap[c.zone]]||0)) zoneMap[c.zone]=lv;
    });

    // 실제 시도 경계 폴리곤
    Object.entries(REAL_ZONE_PATHS).forEach(([zone,coords])=>{
      const hasCtr=centers.some(c=>c.zone===zone);
      if (!hasCtr) return;
      const lv=zoneMap[zone]||"정상";
      const lvl=getLvl(lv);
      const path=coords.map(([lat,lon])=>new kakao.maps.LatLng(lat,lon));
      const polygon=new kakao.maps.Polygon({
        path,
        strokeWeight:2,
        strokeColor:lvl.color,
        strokeOpacity:0.8,
        fillColor:lvl.color,
        fillOpacity:lv==="정상"?0.08:0.22,
      });
      polygon.setMap(kakaoMap);
      polygonsRef.current.push(polygon);

      // 시도명
      const lpos=ZONE_LABEL[zone];
      if (lpos) {
        const el=document.createElement("div");
        el.innerHTML=`<span style="font-size:13px;font-weight:800;color:${lvl.color};text-shadow:0 0 8px #000,0 0 4px #000;pointer-events:none;white-space:nowrap;letter-spacing:-0.5px">${zone}</span>`;
        const ov=new kakao.maps.CustomOverlay({position:new kakao.maps.LatLng(lpos.lat,lpos.lon),content:el,zIndex:1});
        ov.setMap(kakaoMap);
        polygonsRef.current.push(ov);
      }
    });

    // 수도권 밀집 센터 분류
    const PULL_LEFT_IDS  = new Set([8,13,14,15,3,6,5,28]);
    const PULL_RIGHT_IDS = new Set([9,12,10,7,11,4,17,18,21]);

    // ① 비수도권: 지도 위 칩 CustomOverlay
    centers.filter(c=>!PULL_LEFT_IDS.has(c.id)&&!PULL_RIGHT_IDS.has(c.id)).forEach(c=>{
      const lv=c.weather?.level||"정상";
      const lvl=getLvl(lv);
      const feels=c.weather?.feelsLike??"--";
      const dispVal = mapDisplayMode==="temp" ? (c.weather?.temp??"--") : feels;
      const hot=lv==="경고"||lv==="위험";
      const wx=wxIcon(feels);
      const el=document.createElement("div");
      el.innerHTML=`<div style="display:flex;align-items:center;gap:4px;background:linear-gradient(90deg,#0C1828F6,#0F1E36F6);border:1.5px solid ${lvl.color};border-radius:7px;padding:5px 10px 5px 7px;cursor:pointer;white-space:nowrap;box-shadow:0 3px 10px rgba(0,0,0,.8),0 0 ${hot?"12px":"4px"} ${lvl.color}${hot?"66":"22"};"><div style="width:5px;height:5px;border-radius:50%;background:${lvl.color};box-shadow:0 0 5px ${lvl.color};flex-shrink:0"></div><span style="font-size:10px;font-weight:800;color:#E8F4FF">${c.name.replace("물류센터","")}</span><span style="font-size:11px">${wx}</span><span style="font-size:13px;font-weight:900;color:${lvl.color};font-family:monospace">${dispVal}°</span></div>`;
      el.onclick=()=>setMapPopup(c);
      const ov=new kakao.maps.CustomOverlay({position:new kakao.maps.LatLng(c.lat,c.lon),content:el,yAnchor:1.3,zIndex:hot?10:5});
      ov.setMap(kakaoMap);
      overlaysRef.current.push(ov);
    });

    // ② 수도권: 서해/동해 바다 위 칩 + 실제 위치 점 + Polyline 연결
    // 서해 배치 좌표 (위도 내림차순 - 위에서 아래로)
    const leftChipPositions = [
      {id:8,  lat:38.20, lon:124.90}, // 고양
      {id:13, lat:37.98, lon:124.70}, // 고촌
      {id:14, lat:37.76, lon:124.55}, // 김포
      {id:15, lat:37.52, lon:124.42}, // 인천
      {id:3,  lat:37.28, lon:124.55}, // 발안
      {id:6,  lat:37.04, lon:124.72}, // 화성
      {id:5,  lat:36.80, lon:124.92}, // 신오산
      {id:28, lat:36.55, lon:125.15}, // 신평택
    ];
    // 동해 배치 좌표
    const rightChipPositions = [
      {id:9,  lat:38.30, lon:130.20}, // 포천
      {id:12, lat:38.05, lon:130.40}, // 신남양주
      {id:10, lat:37.80, lon:130.30}, // 송파
      {id:7,  lat:37.55, lon:130.20}, // 용인
      {id:11, lat:37.30, lon:130.10}, // 곤지암
      {id:4,  lat:37.05, lon:130.00}, // 남사
      {id:17, lat:35.60, lon:129.90}, // 김해
      {id:18, lat:35.35, lon:130.10}, // 양산
      {id:21, lat:35.10, lon:129.95}, // 부산
    ];
    const allChipPositions=[...leftChipPositions,...rightChipPositions];

    allChipPositions.forEach(({id,lat,lon})=>{
      const c=centers.find(x=>x.id===id); if(!c) return;
      const rn320ChipFeels = (id===28 && rn320Latest) ? calcFeelsLike(rn320Latest.temperature,rn320Latest.humidity,0) : null;
      const lv = rn320ChipFeels!=null ? getHeatLevel(rn320ChipFeels) : (c.weather?.level||"정상");
      const lvl=getLvl(lv);
      const feels = rn320ChipFeels!=null ? rn320ChipFeels : (c.weather?.feelsLike??"--");
      const rn320ChipTemp = (id===28 && rn320Latest) ? rn320Latest.temperature : null;
      const dispTemp = rn320ChipTemp!=null ? rn320ChipTemp : (c.weather?.temp??"--");
      const dispVal = mapDisplayMode==="temp" ? dispTemp : feels;
      const hot=lv==="경고"||lv==="위험";
      const wx=wxIcon(feels);
      const centerPos=new kakao.maps.LatLng(c.lat,c.lon);
      const chipPos=new kakao.maps.LatLng(lat,lon);

      // 실제 위치 점
      const dotEl=document.createElement("div");
      dotEl.innerHTML=`<div style="width:9px;height:9px;border-radius:50%;background:${lvl.color};box-shadow:0 0 7px ${lvl.color};border:2px solid rgba(255,255,255,0.85);cursor:pointer;"></div>`;
      dotEl.onclick=()=>setMapPopup(c);
      const dotOv=new kakao.maps.CustomOverlay({position:centerPos,content:dotEl,yAnchor:0.5,xAnchor:0.5,zIndex:8});
      dotOv.setMap(kakaoMap);
      overlaysRef.current.push(dotOv);

      // 바다 위 칩
      const chipEl=document.createElement("div");
      chipEl.innerHTML=`
        <div style="display:flex;align-items:center;gap:4px;background:linear-gradient(90deg,#0C1828F6,#0F1E36F6);border:1.5px solid ${lvl.color};border-radius:7px;padding:5px 10px 5px 7px;cursor:pointer;white-space:nowrap;box-shadow:0 3px 12px rgba(0,0,0,.8),0 0 ${hot?"12px":"4px"} ${lvl.color}${hot?"66":"22"};">
          <div style="width:5px;height:5px;border-radius:50%;background:${lvl.color};box-shadow:0 0 5px ${lvl.color};flex-shrink:0"></div>
          <span style="font-size:10px;font-weight:800;color:#E8F4FF;">${c.name.replace("물류센터","")}</span>
          <span style="font-size:11px;">${wx}</span>
          <span style="font-size:13px;font-weight:900;color:${lvl.color};font-family:monospace;">${dispVal}°</span>
        </div>`;
      chipEl.onclick=()=>setMapPopup(c);
      const chipOv=new kakao.maps.CustomOverlay({position:chipPos,content:chipEl,yAnchor:0.5,zIndex:10});
      chipOv.setMap(kakaoMap);
      overlaysRef.current.push(chipOv);

      // Polyline: 실제위치 → 칩
      const polyline=new kakao.maps.Polyline({
        path:[centerPos,chipPos],
        strokeColor:lvl.color,
        strokeWeight:1.5,
        strokeOpacity:0.75,
        strokeStyle:"shortdash",
      });
      polyline.setMap(kakaoMap);
      overlaysRef.current.push(polyline);
    });

  },[kakaoMap,centers,rn320Latest,mapDisplayMode]);

  // 모든 마커는 CustomOverlay로 카카오맵에 올라가므로 SVG 인출선 불필요

  // ── 기타
  useEffect(()=>{ fetchAllWeather(); const i=setInterval(fetchAllWeather,5*60*1000); return()=>clearInterval(i); },[fetchAllWeather]);
  useEffect(()=>{
    const fetchRN320=()=>{
      fetch(`/api/sensor/radionode/${RN320_DEVICE_EUI}`).then(r=>r.json()).then(arr=>{
        if (Array.isArray(arr) && arr.length) {
          // 배열 끝(최신)에서부터 거슬러 올라가며 실제 센서값(checkin 프레임 제외)을 찾음
          for (let i=arr.length-1;i>=0;i--) {
            const e = arr[i];
            if (e.decoded && !e.decoded.error && e.decoded.temperature!=null) { setRn320Latest(e.decoded); break; }
          }
        }
      }).catch(()=>{});
    };
    const fetchRN320History=()=>{
      fetch(`/api/sensor/radionode/history/${RN320_DEVICE_EUI}`).then(r=>r.json()).then(h=>setRn320History(h||{})).catch(()=>{});
    };
    fetchRN320(); fetchRN320History();
    const i=setInterval(()=>{ fetchRN320(); fetchRN320History(); },5*60*1000);
    return()=>clearInterval(i);
  },[]);
  useEffect(()=>{
    const now=new Date();
    const ms=(60-now.getMinutes())*60000-now.getSeconds()*1000;
    historyTimerRef.current=setTimeout(()=>{ historyTimerRef.current=setInterval(fetchAllWeather,3600000); },ms);
    return()=>{ clearTimeout(historyTimerRef.current); clearInterval(historyTimerRef.current); };
  },[fetchAllWeather]);

  const levelCounts={정상:0,관심:0,주의:0,경고:0,위험:0};
  centers.forEach(c=>{ if(c.weather?.level) levelCounts[c.weather.level]=(levelCounts[c.weather.level]||0)+1; else levelCounts["정상"]++; });

  const getHourlyAvg=(centerId)=>{
    const data = centerId===28
      ? Object.fromEntries(Object.entries(rn320History).map(([h,d])=>[h,{avgTemp:d.avgTemp,avgHumidity:d.avgHumidity,avgFeels:(d.avgTemp!=null&&d.avgHumidity!=null)?calcFeelsLike(d.avgTemp,d.avgHumidity,0):null,count:d.count}]))
      : (historyData[centerId]||{});
    return Object.entries(data).map(([hour,d])=>({
      hour,
      avgTemp: d.avgTemp!=null ? d.avgTemp : (d.temps ? Math.round((d.temps.reduce((a,b)=>a+b,0)/d.temps.length)*10)/10 : null),
      avgFeels: d.avgFeels!=null ? d.avgFeels : (d.feels ? Math.round((d.feels.reduce((a,b)=>a+b,0)/d.feels.length)*10)/10 : null),
      avgHumidity: d.avgHumidity!=null ? d.avgHumidity : (d.humidities ? Math.round(d.humidities.reduce((a,b)=>a+b,0)/d.humidities.length) : null),
      count:d.count,
    })).filter(x=>x.avgFeels!=null).sort((a,b)=>a.hour.localeCompare(b.hour)).slice(-12);
  };

  // 칩 스타일
  const chipStyle=(c)=>{
    const lv=c.weather?.level||"정상"; const lvl=getLvl(lv); const hot=lv==="경고"||lv==="위험";
    return { display:"flex",alignItems:"center",gap:4,background:"linear-gradient(90deg,#0C1828F6,#0F1E36F6)",border:`1.5px solid ${lvl.color}`,borderRadius:7,padding:"5px 10px 5px 7px",whiteSpace:"nowrap",cursor:"pointer",boxShadow:`0 3px 10px rgba(0,0,0,.7),0 0 ${hot?"10px":"3px"} ${lvl.color}${hot?"55":"22"}`,position:"relative",transition:"transform .15s" };
  };

  return (
    <div style={{fontFamily:"'Noto Sans KR',sans-serif",background:"#0F1F3D",minHeight:"100vh",color:"#F0F6FF"}}>
      {(!authUser || authUser.mustChangePassword || (authUser.role==="최고관리자" && showApprovalPanel)) && (
        <div style={{position:"fixed",inset:0,zIndex:99999}}>
          {!authUser ? <LoginScreen onLogin={handleLogin} /> :
           authUser.mustChangePassword ? <ChangePasswordScreen token={authUser.token} onChanged={handlePasswordChanged} /> :
           <ApprovalScreen token={authUser.token} onClose={()=>setShowApprovalPanel(false)} />}
        </div>
      )}
      <style>{`
        @media (max-width: 768px) {
          .right-panel-980 { display: none !important; }
        }
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;500;700;900&family=JetBrains+Mono:wght@400;700&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        ::-webkit-scrollbar{width:6px;}
        ::-webkit-scrollbar-thumb{background:#2A4E88;border-radius:4px;}
        .tab-btn{background:none;border:none;cursor:pointer;padding:11px 22px;font-family:inherit;font-size:14px;font-weight:600;color:#7BA5D8;border-bottom:2px solid transparent;transition:all .2s;}
        .tab-btn.active{color:#4DA6FF;border-color:#4DA6FF;}
        .badge{display:inline-flex;align-items:center;gap:4px;padding:3px 10px;border-radius:20px;font-size:12px;font-weight:700;}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}
        @keyframes slideIn{from{transform:translateX(110%);opacity:0}to{transform:translateX(0);opacity:1}}
        @keyframes popIn{from{transform:translate(-50%,-50%) scale(.88);opacity:0}to{transform:translate(-50%,-50%) scale(1);opacity:1}}
        .notif{animation:slideIn .3s ease;}
        .stat-num{font-family:'JetBrains Mono',monospace;}
        .history-table{width:100%;border-collapse:collapse;font-size:13px;}
        .history-table th{background:#112240;padding:10px 14px;text-align:left;font-weight:600;color:#7BA5D8;border-bottom:1px solid #274878;}
        .history-table td{padding:10px 14px;border-bottom:1px solid #274878;color:#C8E0FF;}
        .history-table tr:hover td{background:#162B50;}
        .bar{height:4px;border-radius:4px;transition:width .5s ease;}
        .alert-item{background:#162B50;border-left:3px solid;padding:10px 14px;border-radius:0 8px 8px 0;margin-bottom:8px;}
        .chip:hover{transform:scale(1.07)!important;}
      `}</style>

      {/* 플로팅 알림 */}
      <div style={{position:"fixed",top:20,right:20,zIndex:9999,display:"flex",flexDirection:"column",gap:8}}>
        {notifications.map(n=>(
          <div key={n.id} className="notif" style={{background:HEAT_LEVELS[n.level]?.color||"#4DA6FF",color:"#fff",padding:"12px 16px",borderRadius:10,maxWidth:300,fontSize:13,fontWeight:600,boxShadow:"0 4px 20px rgba(0,0,0,.5)"}}>
            🚨 {n.message}
          </div>
        ))}
      </div>

      {/* 헤더 */}
      <header style={{background:"linear-gradient(105deg,#1A3A6E,#135868 55%,#1A6068)",borderBottom:"1px solid #27487880",padding:"18px 28px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <div style={{width:40,height:40,background:"linear-gradient(135deg,#EF4444,#F97316)",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>🌡️</div>
          <div>
            <div style={{fontSize:19,fontWeight:900,letterSpacing:"-0.5px"}}>온열질환 모니터링 시스템</div>
            <div style={{fontSize:11,color:"#A8CCEE",marginTop:3}}>Platform WMS · 물류센터 실시간 현황 · 산업안전보건법 기준</div>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:16}}>
          {lastUpdated&&<div style={{fontSize:12,color:"#7BA5D8",display:"flex",alignItems:"center",gap:6}}><span style={{color:"#4ADEAA",fontSize:8}}>●</span>마지막 갱신: <strong style={{color:"#B8D0F0"}}>{lastUpdated.toLocaleTimeString("ko-KR")}</strong></div>}
          <button onClick={fetchAllWeather} style={{background:"#1E3F70",border:"1px solid #2E5EA0",color:"#B8D0F0",padding:"8px 16px",borderRadius:10,cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:"inherit"}}>🔄 새로고침</button>
          {authUser?.role==="최고관리자" && (
            <button onClick={()=>setShowApprovalPanel(true)} style={{background:"#1E3F70",border:"1px solid #2E5EA0",color:"#B8D0F0",padding:"8px 16px",borderRadius:10,cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:"inherit"}}>👤 가입승인</button>
          )}
          <button onClick={handleLogout} style={{background:"#1E3F70",border:"1px solid #2E5EA0",color:"#B8D0F0",padding:"8px 16px",borderRadius:10,cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:"inherit"}}>🚪 로그아웃</button>
        </div>
      </header>

      {/* 탭 */}
      <div style={{background:"#111E38",borderBottom:"1px solid #274878",padding:"0 28px",display:"flex",gap:2}}>
        {[["dashboard","📊 대시보드"],["history","📈 이력 관리"],["alerts","🔔 알림 로그"]].map(([id,label])=>(
          <button key={id} className={`tab-btn ${activeTab===id?"active":""}`} onClick={()=>setActiveTab(id)}>{label}</button>
        ))}
      </div>

      <main style={{padding:"8px 16px"}}>

        {/* ── 대시보드 탭 ── */}
        <div style={{display:activeTab==="dashboard"?"block":"none"}}>
            {/* 메인: 왼쪽(경보카드+지도) + 오른쪽(분류표+응급조치) */}
            <div style={{display:"flex",gap:10,height:"calc(100vh - 115px)",minHeight:600}}>

              {/* 왼쪽 컬럼: 경보카드 + 지도 */}
              <div style={{flex:1,display:"flex",flexDirection:"column",gap:7,minWidth:0}}>

                {/* 경보 요약 카드 */}
                <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:7,flexShrink:0}}>
                  {[["⬜","정상","31°C 미만","#7BA5D8",levelCounts["정상"]||0],
                    ...Object.entries(HEAT_LEVELS).map(([n,l])=>[l.emoji,n,l.tempRange,l.color,levelCounts[n]||0])
                  ].map(([emoji,name,range,color,count])=>(
                    <div key={name} style={{background:"#162B50",border:`1px solid ${color}28`,borderTop:`3px solid ${color}`,borderRadius:10,padding:"12px 14px"}}>
                      <div style={{fontSize:13,color,fontWeight:700,marginBottom:4}}>{emoji} {name}</div>
                      <div className="stat-num" style={{fontSize:32,fontWeight:900,color,lineHeight:1}}>{count}</div>
                      <div style={{fontSize:11,color:"#3A6090",marginTop:4}}>센터 · {range}</div>
                      <div style={{marginTop:6,background:"#0A1525",borderRadius:3,height:4}}>
                        <div className="bar" style={{background:color,width:`${(count/centers.length)*100}%`}}/>
                      </div>
                    </div>
                  ))}
                </div>

                {/* 지도 */}
                <div style={{flex:1,position:"relative",borderRadius:14,overflow:"hidden",border:"1px solid #1E3A60",minHeight:0}}>
                  <div ref={mapRef} style={{width:"100%",height:"100%",background:"#0A1830"}}/>
                  <div style={{position:"absolute",top:14,left:14,background:"#0C1828F0",border:"1px solid #274878",borderRadius:10,padding:4,display:"flex",gap:2,zIndex:30}}>
                    {[["feels","체감온도"],["temp","온도"]].map(([m,label])=>(
                      <div key={m} onClick={()=>setMapDisplayMode(m)} style={{background:mapDisplayMode===m?"#4DA6FF":"transparent",color:mapDisplayMode===m?"#fff":"#7BA5D8",fontSize:11,fontWeight:700,padding:"6px 14px",borderRadius:7,cursor:"pointer"}}>{label}</div>
                    ))}
                  </div>
                  {!mapReady&&(
                    <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",background:"#0A1830",flexDirection:"column",gap:12}}>
                      <div style={{fontSize:32}}>🗺️</div>
                      <div style={{color:"#4DA6FF",fontSize:14,fontWeight:700}}>지도 로딩 중...</div>
                    </div>
                  )}
                  <div style={{position:"absolute",bottom:14,left:14,background:"#050A14EC",border:"1px solid #1A3050",borderRadius:10,padding:"10px 14px",fontSize:9,zIndex:30,backdropFilter:"blur(4px)"}}>
                    <div style={{color:"#5A80B0",marginBottom:6,fontWeight:700,fontSize:10}}>시도별 체감온도 경보</div>
                    {[["정상","~31°C","#7BA5D8"],["관심","31°C~","#22C55E"],["주의","33°C~","#EAB308"],["경고","35°C~","#F97316"],["위험","38°C~","#EF4444"]].map(([n,r,c])=>(
                      <div key={n} style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                        <div style={{width:14,height:10,borderRadius:2,background:c,opacity:n==="정상"?0.2:0.4,border:`1px solid ${c}`}}/>
                        <span style={{color:c,fontWeight:700,minWidth:28}}>{n}</span>
                        <span style={{color:"#1E3050"}}>{r}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 오른쪽 컬럼: 분류표 + 응급조치 */}
              <div className="right-panel-980" style={{width:980,display:"flex",flexDirection:"column",gap:8,flexShrink:0}}>

                {/* 고용노동부 분류표 */}
                <div style={{background:"#111827",border:"1px solid #274878",borderRadius:12,padding:"10px 12px",flexShrink:0}}>
                  <div style={{fontSize:11,fontWeight:700,marginBottom:8,color:"#7BA5D8"}}>📋 고용노동부 온열질환 예방 조치기준</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:6}}>
                    {Object.entries(HEAT_LEVELS).map(([name,lvl])=>(
                      <div key={name} style={{background:"#0D1525",borderRadius:8,padding:"7px 9px",borderTop:`2px solid ${lvl.color}`}}>
                        <div style={{fontSize:14,fontWeight:800,color:lvl.color,marginBottom:6}}>{lvl.emoji} {name} · {lvl.tempRange}</div>
                        {lvl.actions.map((a,i)=>(
                          <div key={i} style={{fontSize:12,color:"#7A9AC0",display:"flex",gap:5,marginBottom:4,lineHeight:1.6}}>
                            <span style={{color:lvl.color}}>▶</span>{a}
                          </div>
                        ))}
                        {lvl.telegramAlert&&<div style={{marginTop:6,fontSize:12,color:"#4DA6FF"}}>📱 텔레그램 자동 알림</div>}
                      </div>
                    ))}
                  </div>
                </div>

                {/* 응급조치 요령 HTML/CSS */}
                <div style={{flex:1,background:"#fff",borderRadius:12,border:"1px solid #274878",overflow:"hidden",minHeight:0,display:"flex",flexDirection:"column"}}>
                  <div style={{background:"linear-gradient(105deg,#E8D5F5,#D5E8F5)",padding:"7px 14px",textAlign:"center",borderBottom:"2px solid #C9A8E8",flexShrink:0}}>
                    <div style={{fontSize:14,fontWeight:900,color:"#5B2D8E"}}>🌡️ 온열질환 응급조치 요령(질병관리청)</div>
                  </div>
                  <div style={{padding:"10px 12px",flex:1,overflowY:"auto"}}>
                    <div style={{marginBottom:8}}>
                      <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:5}}>
                        <div style={{width:14,height:14,background:"#5B2D8E",borderRadius:3,display:"flex",alignItems:"center",justifyContent:"center"}}><span style={{color:"#fff",fontSize:8,fontWeight:700}}>▶</span></div>
                        <span style={{fontSize:13,fontWeight:800,color:"#333"}}>온열질환 증상</span>
                      </div>
                      <div style={{background:"#F0F8F0",border:"1px solid #90D090",borderRadius:6,padding:"5px 9px",fontSize:12,color:"#2D5A2D",lineHeight:1.7}}>
                        ✅ 평소보다 <strong>높은 체온</strong>, 땀을 많이 흘림, 어지러움, 메스꺼움, 근육 경련, 의식 저하
                      </div>
                    </div>
                    <div style={{marginBottom:8}}>
                      <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:5}}>
                        <div style={{width:14,height:14,background:"#5B2D8E",borderRadius:3,display:"flex",alignItems:"center",justifyContent:"center"}}><span style={{color:"#fff",fontSize:8,fontWeight:700}}>▶</span></div>
                        <span style={{fontSize:13,fontWeight:800,color:"#333"}}>온열질환 조치 요령</span>
                      </div>
                      {["시원한 장소로 이동하세요","옷을 헐렁하게 하고 몸에 시원한 물을 적셔 부채나 선풍기 등으로 몸을 식히세요","시원한 물을 섭취하여 수분을 보충하세요 ※ 의식이 없을 경우 수분섭취 절대 금지","휴식을 취하거나 의료적인 도움을 받으세요"].map((t,i)=>(
                        <div key={i} style={{display:"flex",gap:5,marginBottom:4,fontSize:12,color:"#333",lineHeight:1.6}}>
                          <span style={{color:"#2E7D32",fontSize:10,flexShrink:0}}>✅</span>{t}
                        </div>
                      ))}
                    </div>
                    <div style={{background:"#F8F8F8",borderRadius:8,padding:"8px",border:"1px solid #DDD"}}>
                      <div style={{display:"flex",alignItems:"center",gap:3,marginBottom:6,flexWrap:"wrap"}}>
                        <div style={{background:"#B2EBF2",border:"2px solid #00ACC1",borderRadius:6,padding:"4px 6px",fontSize:8,fontWeight:700,color:"#006064",textAlign:"center"}}>의식<br/>유무</div>
                        <span style={{fontSize:9,color:"#333",fontWeight:700}}>있음 →</span>
                        {["시원한 장소로 이동","몸을 시원하게 함","수분 섭취"].map((s,i)=>(
                          <div key={i} style={{display:"flex",alignItems:"center",gap:2}}>
                            <div style={{background:"#E3F2FD",border:"1.5px solid #1976D2",borderRadius:5,padding:"3px 5px",fontSize:7,color:"#0D47A1",textAlign:"center"}}>{s}</div>
                            {i<2&&<span style={{fontSize:9,color:"#666"}}>→</span>}
                          </div>
                        ))}
                        <span style={{fontSize:9,color:"#666"}}>→</span>
                        <div style={{background:"#FFF8E1",border:"1.5px solid #F9A825",borderRadius:5,padding:"3px 5px",fontSize:7,color:"#E65100",textAlign:"center"}}>증상 지속시</div>
                      </div>
                      <div style={{borderTop:"1px dashed #CCC",paddingTop:6}}>
                        <div style={{display:"flex",alignItems:"center",gap:3,flexWrap:"wrap"}}>
                          <div style={{background:"#FFCDD2",border:"2px solid #E53935",borderRadius:6,padding:"4px 6px",fontSize:8,fontWeight:700,color:"#B71C1C",textAlign:"center"}}>의식<br/>없음</div>
                          <div style={{background:"#FFCDD2",border:"2px solid #E53935",borderRadius:6,padding:"4px 6px",fontSize:8,fontWeight:700,color:"#B71C1C",textAlign:"center"}}>119<br/>요청</div>
                          <span style={{fontSize:9,color:"#333"}}>→</span>
                          {["시원한 장소로 이동","몸을 시원하게 함","병원으로 후송"].map((s,i)=>(
                            <div key={i} style={{display:"flex",alignItems:"center",gap:2}}>
                              <div style={{background:"#FCE4EC",border:"1.5px solid #E91E63",borderRadius:5,padding:"3px 5px",fontSize:7,color:"#880E4F",textAlign:"center"}}>{s}</div>
                              {i<2&&<span style={{fontSize:9,color:"#666"}}>→</span>}
                            </div>
                          ))}
                          <span style={{fontSize:9,color:"#666"}}>← 119</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 공기질(CO2) · 조도(lux) 관리 기준 */}
                <div style={{background:"#111827",border:"1px solid #274878",borderRadius:12,padding:"10px 12px",flexShrink:0}}>
                  <div style={{fontSize:11,fontWeight:700,marginBottom:8,color:"#7BA5D8"}}>📊 공기질(CO2) · 조도(lux) 관리 기준</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:0}}>
                    <div style={{borderRight:"1px solid #1E3A60",paddingRight:10}}>
                      <div style={{fontSize:10,fontWeight:700,color:"#4ADEAA",marginBottom:6}}>공기질(CO2)</div>
                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:10}}>
                        <tbody>
                          <tr><td style={{color:"#5A80B0",padding:"3px 0",borderBottom:"1px solid #162B48"}}>구분</td><td style={{color:"#5A80B0",padding:"3px 0",borderBottom:"1px solid #162B48",textAlign:"right"}}>신체 영향</td></tr>
                          {[["400~1,000","쾌적하고 건강한 정상","#22C55E"],["1,000~2,000","답답함, 졸음, 피로감","#EAB308"],["2,000~3,000","두통, 집중력 저하","#F97316"],["3,000 이상","심한 두통, 현기증","#EF4444"]].map(([r,d,cc],i)=>(
                            <tr key={i}><td style={{color:cc,padding:"4px 4px 4px 0",fontWeight:700}}>{r}</td><td style={{color:"#B8D0F0",padding:"4px 0",textAlign:"right"}}>{d}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div style={{paddingLeft:10}}>
                      <div style={{fontSize:10,fontWeight:700,color:"#4ADEAA",marginBottom:6}}>조도(lux)</div>
                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:10}}>
                        <tbody>
                          <tr><td style={{color:"#5A80B0",padding:"3px 0",borderBottom:"1px solid #162B48"}}>기준</td><td style={{color:"#5A80B0",padding:"3px 0",borderBottom:"1px solid #162B48",textAlign:"right"}}>존 구분</td></tr>
                          {[["100 lux","통로"],["150 lux","일반 보관구역"],["300 lux","피킹구역"],["500 lux","정밀피킹"]].map(([r,d],i)=>(
                            <tr key={i}><td style={{color:"#E8F4FF",padding:"4px 4px 4px 0",fontWeight:700}}>{r}</td><td style={{color:"#B8D0F0",padding:"4px 0",textAlign:"right"}}>{d}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 팝업 */}
            {mapPopup&&(()=>{
              const c=centers.find(x=>x.id===mapPopup.id)||mapPopup;
              const rn320PopupFeels = (c.id===28 && rn320Latest) ? calcFeelsLike(rn320Latest.temperature,rn320Latest.humidity,0) : null;
              const lv = rn320PopupFeels!=null ? getHeatLevel(rn320PopupFeels) : (c.weather?.level||"정상"); const lvl=getLvl(lv);

              // 시간별 이력 데이터 구성 (historyData에서 최근 12개 + 현재)
              const hist = c.id===28
                ? Object.fromEntries(Object.entries(rn320History).map(([h,d])=>[h,{avgFeels:(d.avgTemp!=null&&d.avgHumidity!=null)?calcFeelsLike(d.avgTemp,d.avgHumidity,0):null}]))
                : (historyData[c.id]||{});
              const hourlyArr=Object.entries(hist).map(([hour,d])=>({
                hour:hour.slice(11,16), // "HH:MM"
                date:hour.slice(5,10),  // "MM-DD"
                fullHour:hour, // 정렬용 전체 날짜+시간 문자열 (자정 경계 순서 꼬임 방지)
                feels: d.avgFeels!=null ? d.avgFeels : (d.feels ? Math.round((d.feels.reduce((a,b)=>a+b,0)/d.feels.length)*10)/10 : null),
              })).filter(x=>x.feels!=null).sort((a,b)=>a.fullHour.localeCompare(b.fullHour)).slice(-12);

              // 현재 항목 추가 (신평택은 RN320 실측 기준)
              const currentEntry = c.id===28
                ? (rn320Latest ? {
                    hour:"현재", date:new Date().toLocaleDateString("ko-KR",{month:"2-digit",day:"2-digit"}).replace(". ","/",[]).replace(".","",[]),
                    feels: calcFeelsLike(rn320Latest.temperature, rn320Latest.humidity, 0), isCurrent:true
                  } : null)
                : (c.weather?.feelsLike!=null?{
                    hour:"현재", date:new Date().toLocaleDateString("ko-KR",{month:"2-digit",day:"2-digit"}).replace(". ","/",[]).replace(".","",[]),
                    feels:c.weather.feelsLike, isCurrent:true
                  }:null);
              const chartData=[...hourlyArr,...(currentEntry?[currentEntry]:[])];

              const maxF=chartData.length?Math.max(...chartData.map(h=>h.feels)):38;
              const minF=chartData.length?Math.min(...chartData.map(h=>h.feels)):20;
              const range=maxF-minF||1;
              const chartAvg=chartData.length?Math.round(chartData.reduce((s,h)=>s+h.feels,0)/chartData.length*10)/10:null;

              // 날짜 변경 지점
              const today=new Date().toLocaleDateString("ko-KR",{month:"2-digit",day:"2-digit"}).replace(". ","/",[]).replace(".","",[]);
              const firstTodayIdx=chartData.findIndex(h=>h.date===today&&!h.isCurrent);

              // 최근 2시간 평균
              const recent=hourlyArr.slice(-2);
              const avgFeels=recent.length?Math.round(recent.reduce((s,h)=>s+h.feels,0)/recent.length*10)/10:null;
              const avgLevel=avgFeels!=null?getHeatLevel(avgFeels):"정상";
              const avgLvl=getLvl(avgLevel);

              return (
                <>
                  <div onClick={()=>setMapPopup(null)} style={{position:"fixed",inset:0,background:"rgba(3,7,18,.72)",zIndex:200,backdropFilter:"blur(3px)"}}/>
                  <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",zIndex:201,width:480,background:"linear-gradient(160deg,#162B50,#091525)",border:`2px solid ${lvl.color}`,borderRadius:18,boxShadow:`0 28px 64px rgba(0,0,0,.8),0 0 48px ${lvl.color}20`,overflow:"hidden",animation:"popIn .22s cubic-bezier(.22,1,.36,1)"}}>
                    {/* 헤더 */}
                    <div style={{background:`linear-gradient(105deg,${lvl.color}1E,${lvl.color}06)`,borderBottom:`1px solid ${lvl.color}2E`,padding:"14px 16px",display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                      <div>
                        <div style={{fontSize:15,fontWeight:900,marginBottom:4}}>📦 {c.name}</div>
                        <div style={{display:"flex",gap:7,alignItems:"center"}}>
                          <span className="badge" style={{background:`${lvl.color}1E`,color:lvl.color,border:`1px solid ${lvl.color}40`}}>{lvl.emoji} {lv}</span>
                          <span style={{fontSize:11,color:"#4A6890"}}>👥 {c.workers}명 · {c.zone}</span>
                        </div>
                      </div>
                      <button onClick={()=>setMapPopup(null)} style={{background:"#162B48",border:"1px solid #274878",color:"#7BA5D8",width:26,height:26,borderRadius:6,cursor:"pointer",fontSize:13,fontFamily:"inherit"}}>✕</button>
                    </div>

                    <div style={{padding:"14px 16px"}}>
                      {/* 현재 수치 */}
                      {c.id===28 && rn320Latest && (
                        <>
                          <div style={{fontSize:9,color:"#4ADEAA",fontWeight:700,marginBottom:3}}>실측 (RN320)</div>
                          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr",gap:7,marginBottom:8}}>
                            {[
                              {icon:"🌡️",label:"현재기온",val:`${rn320Latest.temperature}°C`,hi:false},
                              {icon:"🤒",label:"체감온도",val:`${calcFeelsLike(rn320Latest.temperature,rn320Latest.humidity,c.weather?.windSpeed||0)}°C`,hi:true},
                              {icon:"💧",label:"습도",val:`${rn320Latest.humidity}%`,hi:false},
                              {icon:"🫧",label:"공기질(CO2)",val:`${rn320Latest.co2}ppm`,hi:false},
                              {icon:"☀️",label:"조도",val:`${rn320Latest.lux}lux`,hi:false},
                            ].map(s=>(
                              <div key={s.label} style={{background:s.hi?`${lvl.color}14`:"#091525",border:`1px solid ${s.hi?lvl.color+"48":"#162B48"}`,borderRadius:8,padding:"8px 4px",textAlign:"center"}}>
                                <div style={{fontSize:9,color:"#E8F4FF",marginBottom:3}}>{s.icon}<br/>{s.label}</div>
                                <div className="stat-num" style={{fontSize:14,fontWeight:900,color:s.hi?lvl.color:"#F0F6FF"}}>{s.val}</div>
                              </div>
                            ))}
                          </div>
                          <div style={{fontSize:10,color:"#7BA5D8",fontWeight:700,marginBottom:4}}>기상청</div>
                        </>
                      )}
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:4,marginBottom:12}}>
                        {[
                          {icon:"🌡️",label:"현재기온",val:`${c.weather?.temp??"--"}°C`},
                          {icon:"🤒",label:"체감온도",val:`${c.weather?.feelsLike??"--"}°C`},
                          {icon:"💧",label:"습도",val:`${c.weather?.humidity??"--"}%`},
                          {icon:"💨",label:"풍속",val:`${c.weather?.windSpeed??"--"}m/s`},
                        ].map(s=>(
                          <div key={s.label} style={{background:"#0B1830",borderRadius:6,padding:"5px 2px",textAlign:"center"}}>
                            <div style={{fontSize:7.5,color:"#8AAAD0",marginBottom:1}}>{s.label}</div>
                            <div className="stat-num" style={{fontSize:11,fontWeight:900,color:"#E8F4FF"}}>{s.val}</div>
                          </div>
                        ))}
                      </div>

                      {/* 시간별 차트 */}
                      <div style={{background:"#091525",border:"1px solid #162B48",borderRadius:10,padding:"12px 14px",marginBottom:12}}>
                        <div style={{fontSize:11,fontWeight:700,color:"#5A80B0",marginBottom:8}}>{c.id===28?"📈 실측 체감온도 시간별 추이 (RN320)":"📈 체감온도 시간별 추이"}</div>
                        {chartData.length>0?(
                          <>
                            {/* 날짜 레이블 */}
                            <div style={{display:"flex",position:"relative",height:14,marginBottom:3}}>
                              {chartData.length>0&&(
                                <div style={{position:"absolute",left:0,fontSize:9,fontWeight:700,color:"#5A80B0"}}>
                                  {chartData[0].date} ▼
                                </div>
                              )}
                              {firstTodayIdx>0&&(
                                <div style={{position:"absolute",left:`${(firstTodayIdx/chartData.length)*100}%`,fontSize:9,fontWeight:700,color:"#4ADEAA"}}>
                                  {today} ▼
                                </div>
                              )}
                              {chartAvg!=null&&(
                                <div style={{position:"absolute",right:0,fontSize:9,fontWeight:700,color:"#8AAAD0"}}>
                                  평균 {chartAvg}°C
                                </div>
                              )}
                            </div>
                            {/* 바 */}
                            <div style={{display:"flex",alignItems:"flex-end",gap:2,height:56}}>
                              {chartData.map((h,i)=>{
                                const pct=(h.feels-minF+1)/(range+3);
                                const barColor=getLvl(getHeatLevel(h.feels)).color;
                                const isToday=h.date===today&&!h.isCurrent;
                                return (
                                  <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:1,position:"relative"}}>
                                    {i===firstTodayIdx&&firstTodayIdx>0&&(
                                      <div style={{position:"absolute",left:0,top:0,bottom:0,width:1,background:"#4ADEAA",opacity:.5}}/>
                                    )}
                                    <div style={{width:"100%",background:barColor,borderRadius:"2px 2px 0 0",height:`${Math.max(pct*52,3)}px`,opacity:h.isCurrent?1:isToday?0.85:0.5,boxShadow:h.isCurrent?`0 0 8px ${barColor}`:"",border:h.isCurrent?`1px solid ${barColor}`:""}}/>
                                    <div style={{fontSize:6.5,color:h.isCurrent?"#4ADEAA":isToday?"#5A80B0":"#2A4060",fontWeight:h.isCurrent?700:400}}>
                                      {h.isCurrent?"현재":h.hour}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        ):(
                          <div style={{textAlign:"center",color:"#3A5880",fontSize:12,padding:"16px 0"}}>⏳ 데이터 수집 중...</div>
                        )}
                        {/* 범례 */}
                        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:6,paddingTop:6,borderTop:"1px solid #162B48"}}>
                          {[["관심","31°~","#22C55E"],["주의","33°~","#EAB308"],["경고","35°~","#F97316"],["위험","38°~","#EF4444"]].map(([n,r,bc])=>(
                            <div key={n} style={{display:"flex",alignItems:"center",gap:3,fontSize:9,color:"#3A5880"}}>
                              <div style={{width:8,height:8,borderRadius:2,background:bc,opacity:.7}}/>
                              <span style={{color:bc}}>{n}</span><span>{r}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* 최근 2시간 평균 */}
                      {avgFeels!=null?(
                        <div style={{background:`${avgLvl.color}0E`,border:`1px solid ${avgLvl.color}35`,borderRadius:10,padding:"11px 14px",marginBottom:12}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                            <div style={{fontSize:11,fontWeight:800,color:avgLvl.color}}>{c.id===28?"🕐 최근 2시간 실측 평균 온열단계":"🕐 최근 2시간 평균 온열단계"}</div>
                            <span style={{background:`${avgLvl.color}20`,color:avgLvl.color,border:`1px solid ${avgLvl.color}50`,padding:"2px 9px",borderRadius:20,fontSize:11,fontWeight:700}}>
                              {avgLvl.emoji} {avgLevel}
                            </span>
                          </div>
                          <div style={{display:"flex",alignItems:"baseline",gap:10,marginBottom:recent.length?6:0}}>
                            <div className="stat-num" style={{fontSize:22,fontWeight:900,color:avgLvl.color}}>{avgFeels}°C</div>
                            <div style={{fontSize:10,color:"#5A80B0"}}>{recent.map(h=>`${h.hour} ${h.feels}°`).join(" · ")}</div>
                          </div>
                          {avgLevel!=="정상"&&getLvl(avgLevel).actions&&(
                            <div style={{paddingTop:7,borderTop:`1px solid ${avgLvl.color}25`}}>
                              {getLvl(avgLevel).actions.map((a,i)=>(
                                <div key={i} style={{fontSize:10,color:"#8AAAD0",display:"flex",gap:5,marginBottom:3,lineHeight:1.5}}>
                                  <span style={{color:avgLvl.color}}>▶</span>{a}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ):(
                        <div style={{background:"#091525",border:"1px solid #162B48",borderRadius:10,padding:"10px 14px",marginBottom:12,textAlign:"center",color:"#3A5880",fontSize:12}}>
                          ⏳ 평균 데이터 수집 중... (1시간 후 표시)
                        </div>
                      )}

                      {c.weather?.simulated&&<div style={{fontSize:11,color:"#F59E0B",textAlign:"center",marginBottom:10}}>⚠️ 시뮬레이션 데이터</div>}
                      <button onClick={()=>{setSelectedCenter(c);setMapPopup(null);setActiveTab("history");}}
                        style={{width:"100%",background:`linear-gradient(105deg,${lvl.color}22,${lvl.color}0E)`,border:`1px solid ${lvl.color}48`,color:lvl.color,padding:"9px 0",borderRadius:9,cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit"}}>
                        📈 이력 상세 보기
                      </button>
                    </div>
                  </div>
                </>
              );
            })()}



        </div>

        {/* ── 이력 관리 탭 ── */}
        {activeTab==="history"&&(
          <div>
            <div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap"}}>
              <button onClick={()=>setSelectedCenter(null)} style={{background:!selectedCenter?"#2E86D4":"#1E3F70",border:`1px solid ${!selectedCenter?"#2E86D4":"#2E5EA0"}`,color:!selectedCenter?"#0F1F3D":"#B8D0F0",padding:"8px 16px",borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:700}}>전체</button>
              {centers.map(c=>(
                <button key={c.id} onClick={()=>setSelectedCenter(c)} style={{background:selectedCenter?.id===c.id?"#2E86D4":"#1E3F70",border:`1px solid ${selectedCenter?.id===c.id?"#2E86D4":"#2E5EA0"}`,color:selectedCenter?.id===c.id?"#0F1F3D":"#B8D0F0",padding:"8px 16px",borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:700}}>{c.name}</button>
              ))}
            </div>
            {(selectedCenter?[selectedCenter]:centers).map(c=>{
              const hourly=getHourlyAvg(c.id);
              const rn320CurrentFeels = (c.id===28 && rn320Latest) ? calcFeelsLike(rn320Latest.temperature,rn320Latest.humidity,c.weather?.windSpeed||0) : null;
              return (
                <div key={c.id} style={{background:"#162B50",border:"1px solid #274878",borderRadius:12,padding:20,marginBottom:16}}>
                  <div style={{fontWeight:700,fontSize:15,marginBottom:16}}>📦 {c.name} — 1시간 단위 평균</div>
                  {hourly.length===0?(
                    <div style={{color:"#7BA5D8",fontSize:13,padding:"20px 0",textAlign:"center"}}>⏳ 데이터 수집 중...</div>
                  ):(
                    <div style={{overflowX:"auto"}}>
                      <table className="history-table">
                        <thead><tr><th>시간대</th><th>평균기온</th><th>평균체감온도</th><th>평균습도</th><th>수집횟수</th><th>온열단계</th></tr></thead>
                        <tbody>
                          {hourly.map(row=>{
                            const level=getHeatLevel(row.avgFeels); const lvl=getLvl(level);
                            return (
                              <tr key={row.hour}>
                                <td className="stat-num" style={{color:"#7BA5D8"}}>{row.hour}</td>
                                <td className="stat-num">{row.avgTemp}°C</td>
                                <td className="stat-num" style={{color:lvl.color,fontWeight:700}}>{row.avgFeels}°C</td>
                                <td className="stat-num">{row.avgHumidity}%</td>
                                <td className="stat-num" style={{color:"#7BA5D8"}}>{row.count}회</td>
                                <td><span className="badge" style={{background:`${lvl.color}20`,color:lvl.color}}>{lvl.emoji} {level}</span></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {c.weather&&(
                    <div style={{marginTop:12,padding:12,background:"#112240",borderRadius:8,fontSize:12,color:"#7BA5D8",display:"flex",gap:20,border:"1px solid #274878"}}>
                      <span>현재: 기온 <strong style={{color:"#F0F6FF"}}>{rn320CurrentFeels!=null ? rn320Latest.temperature : c.weather.temp}°C</strong></span>
                      <span>체감 <strong style={{color:getLvl(rn320CurrentFeels!=null ? getHeatLevel(rn320CurrentFeels) : c.weather.level).color}}>{rn320CurrentFeels!=null ? rn320CurrentFeels : c.weather.feelsLike}°C</strong></span>
                      <span>습도 <strong style={{color:"#F0F6FF"}}>{rn320CurrentFeels!=null ? rn320Latest.humidity : c.weather.humidity}%</strong></span>
                      {c.weather.simulated&&<span style={{color:"#F59E0B"}}>⚠️ 시뮬레이션</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── 알림 탭 ── */}
        {activeTab==="alerts"&&(
          <div style={{display:"grid",gridTemplateColumns:"420px 1fr",gap:20,alignItems:"start"}}>
            {/* 왼쪽: 수신자 관리 */}
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              <div style={{background:"#111827",border:"1px solid #274878",borderRadius:14,padding:18}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <div style={{fontWeight:700,fontSize:14}}>📧 이메일 일일 리포트 받기</div>
                  <div onClick={()=>setEmailReportEnabled(!emailReportEnabled)} style={{width:40,height:22,background:emailReportEnabled?"#4DA6FF":"#274878",borderRadius:12,position:"relative",cursor:"pointer",flexShrink:0}}>
                    <div style={{width:18,height:18,background:"#fff",borderRadius:"50%",position:"absolute",top:2,left:emailReportEnabled?20:2,transition:"left .15s"}}/>
                  </div>
                </div>
                <div style={{fontSize:11,color:"#7A9AC0",marginBottom:10}}>매일 00:05 담당 센터의 전일 체감온도 기록지(엑셀)가 이메일로 발송됩니다.</div>
                <input value={emailReportEmail} onChange={e=>setEmailReportEmail(e.target.value)} placeholder="이메일 주소 입력" style={{width:"100%",background:"#0B1830",border:"1px solid #274878",borderRadius:7,padding:9,fontSize:12,color:"#E8F4FF",boxSizing:"border-box",marginBottom:8}}/>
                <div onClick={saveEmailReportSettings} style={{background:"#4DA6FF",color:"#fff",textAlign:"center",fontSize:12,fontWeight:700,padding:8,borderRadius:7,cursor:"pointer"}}>{emailReportSaved?"✅ 저장됨":"저장"}</div>
              </div>
              <div style={{background:"linear-gradient(105deg,#1A3A6E,#135868)",border:"1px solid #4DA6FF40",borderRadius:14,padding:18}}>
                <div style={{fontWeight:800,fontSize:14,marginBottom:8}}>🤖 챗봇으로 Chat ID 자동 확인</div>
                <div style={{fontSize:12,color:"#A8CCEE",lineHeight:1.7,marginBottom:12}}>텔레그램에서 봇을 찾아 <strong style={{color:"#4ADEAA"}}>/start</strong> 입력 → Chat ID 자동 안내</div>
                <button onClick={autoFetchUsers} disabled={fetchingUsers} style={{background:"#1E3F70",border:"1px solid #4DA6FF60",color:"#4DA6FF",padding:"8px 16px",borderRadius:9,cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit",width:"100%"}}>
                  {fetchingUsers?"⏳ 조회 중...":"🔄 봇 대화자 자동 불러오기"}
                </button>
              </div>
              <div style={{background:"#162B50",border:"1px solid #274878",borderRadius:14,padding:20}}>
                <div style={{fontWeight:700,fontSize:14,marginBottom:16}}>➕ 수신자 등록</div>
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  <div>
                    <div style={{fontSize:11,color:"#7BA5D8",marginBottom:4}}>이름</div>
                    <input placeholder="예: 홍길동 안전관리자" value={newRecipient.name} onChange={e=>setNewRecipient(p=>({...p,name:e.target.value}))} style={{width:"100%",background:"#112240",border:"1px solid #274878",borderRadius:8,padding:"9px 12px",color:"#F0F6FF",fontSize:13,outline:"none",fontFamily:"inherit"}}/>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:"#7BA5D8",marginBottom:4}}>Chat ID <span style={{color:"#4ADEAA",marginLeft:6}}>← /start 후 받은 숫자</span></div>
                    <input placeholder="예: 123456789" value={newRecipient.chatId} onChange={e=>setNewRecipient(p=>({...p,chatId:e.target.value}))} style={{width:"100%",background:"#112240",border:"1px solid #274878",borderRadius:8,padding:"9px 12px",color:"#F0F6FF",fontSize:13,outline:"none",fontFamily:"monospace"}}/>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:"#7BA5D8",marginBottom:6}}>역할</div>
                    <div style={{display:"flex",gap:8}}>
                      {["전체관리자","센터관리자"].map(role=>(
                        <button key={role} onClick={()=>setNewRecipient(p=>({...p,role,assignedCenters:[]}))} style={{flex:1,padding:"9px 0",borderRadius:9,cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"inherit",border:"2px solid",background:newRecipient.role===role?(role==="전체관리자"?"#1E3F70":"#2A1A40"):"#112240",borderColor:newRecipient.role===role?(role==="전체관리자"?"#4DA6FF":"#A78BFA"):"#274878",color:newRecipient.role===role?(role==="전체관리자"?"#4DA6FF":"#A78BFA"):"#7BA5D8"}}>
                          {role==="전체관리자"?"🌐 전체관리자":"📦 센터관리자"}
                        </button>
                      ))}
                    </div>
                  </div>
                  {newRecipient.role==="센터관리자"&&(
                    <div>
                      <div style={{fontSize:11,color:"#7BA5D8",marginBottom:6}}>담당 센터 ({newRecipient.assignedCenters.length}개)</div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:6,maxHeight:160,overflowY:"auto"}}>
                        {LOGISTICS_CENTERS.map(c=>{
                          const on=newRecipient.assignedCenters.includes(c.id);
                          return <button key={c.id} onClick={()=>setNewRecipient(p=>({...p,assignedCenters:on?p.assignedCenters.filter(x=>x!==c.id):[...p.assignedCenters,c.id]}))} style={{padding:"4px 10px",borderRadius:6,cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:"inherit",border:"1px solid",background:on?"#2A1A40":"#112240",borderColor:on?"#A78BFA":"#274878",color:on?"#C4B5FD":"#7BA5D8"}}>{c.name.replace("물류센터","")}</button>;
                        })}
                      </div>
                    </div>
                  )}
                  {recipientError&&<div style={{fontSize:11,color:"#F87171",padding:"6px 10px",background:"#EF444420",borderRadius:6}}>⚠️ {recipientError}</div>}
                  <button onClick={addRecipient} style={{background:"linear-gradient(105deg,#1A3A6E,#135868)",border:"1px solid #4DA6FF50",color:"#F0F6FF",padding:"10px 0",borderRadius:10,cursor:"pointer",fontSize:14,fontWeight:700,fontFamily:"inherit"}}>+ 수신자 등록</button>
                </div>
              </div>
              <div style={{background:"#162B50",border:"1px solid #274878",borderRadius:14,padding:20}}>
                <div style={{fontWeight:700,fontSize:14,marginBottom:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span>등록된 수신자</span>
                  <button onClick={loadRecipients} style={{background:"#1E3F70",border:"1px solid #2E5EA0",color:"#4DA6FF",width:26,height:26,borderRadius:6,cursor:"pointer",fontSize:12}}>🔄</button>
                </div>
                {recipientsLoading?<div style={{textAlign:"center",color:"#7BA5D8",fontSize:13,padding:"20px 0"}}>⏳ 불러오는 중...</div>:recipients.length===0?<div style={{textAlign:"center",color:"#7BA5D8",fontSize:13,padding:"20px 0"}}>등록된 수신자가 없습니다</div>:(
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {recipients.map(r=>{
                      const isGlobal=r.role==="전체관리자";
                      const assignedNames=(r.assignedCenters||[]).map(id=>LOGISTICS_CENTERS.find(c=>c.id===id)?.name?.replace("물류센터","")||id);
                      return (
                        <div key={r.id} style={{background:"#112240",border:`1px solid ${r.active?(isGlobal?"#4DA6FF30":"#A78BFA30"):"#274878"}`,borderRadius:10,padding:"12px 14px",opacity:r.active?1:0.5}}>
                          <div style={{display:"flex",alignItems:"center",gap:10}}>
                            <div onClick={()=>toggleRecipient(r.id,r.active)} style={{width:36,height:20,borderRadius:10,background:r.active?(isGlobal?"#1E3F70":"#2A1A40"):"#1E2A40",border:`2px solid ${r.active?(isGlobal?"#4DA6FF":"#A78BFA"):"#274878"}`,cursor:"pointer",position:"relative",flexShrink:0}}>
                              <div style={{width:12,height:12,borderRadius:"50%",background:r.active?(isGlobal?"#4DA6FF":"#A78BFA"):"#4A6090",position:"absolute",top:2,left:r.active?18:2,transition:"left .2s"}}/>
                            </div>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                                <span style={{fontWeight:700,fontSize:13}}>{r.name}</span>
                                <span style={{fontSize:10,padding:"2px 7px",borderRadius:10,fontWeight:700,background:isGlobal?"#1E3F70":"#2A1A40",color:isGlobal?"#4DA6FF":"#A78BFA",border:`1px solid ${isGlobal?"#4DA6FF50":"#A78BFA50"}`}}>{isGlobal?"🌐 전체":"📦 센터"}</span>
                              </div>
                              <div style={{fontSize:11,color:"#7BA5D8",fontFamily:"monospace"}}>ID: {r.chatId}</div>
                              {!isGlobal&&assignedNames.length>0&&<div style={{fontSize:10,color:"#A78BFA",marginTop:3}}>{assignedNames.join(" · ")}</div>}
                              <div style={{fontSize:10,color:r.dailyReportEnabled?"#4ADEAA":"#4A5A78",marginTop:3}}>📧 이메일 리포트: {r.dailyReportEnabled?`ON (${r.dailyReportEmail||"미입력"})`:"OFF"}</div>
                            </div>
                            <div style={{display:"flex",gap:5,flexShrink:0}}>
                              <button onClick={()=>sendTestMessage(r)} title="테스트" style={{background:"#1E3F70",border:"1px solid #2E5EA0",color:"#4DA6FF",width:28,height:28,borderRadius:7,cursor:"pointer",fontSize:12}}>✉</button>
                              <button onClick={()=>removeRecipient(r.id)} title="삭제" style={{background:"#3A1A1A",border:"1px solid #7A2020",color:"#F87171",width:28,height:28,borderRadius:7,cursor:"pointer",fontSize:12}}>✕</button>
                            </div>
                          </div>
                          {!isGlobal&&editingId===r.id&&(
                            <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid #274878"}}>
                              <div style={{fontSize:11,color:"#A78BFA",marginBottom:6}}>담당 센터 편집</div>
                              <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                                {LOGISTICS_CENTERS.map(c=>{
                                  const on=(r.assignedCenters||[]).includes(c.id);
                                  return <button key={c.id} onClick={()=>toggleCenterAssign(r.id,c.id)} style={{padding:"3px 8px",borderRadius:5,cursor:"pointer",fontSize:10,fontWeight:600,fontFamily:"inherit",border:"1px solid",background:on?"#2A1A40":"#112240",borderColor:on?"#A78BFA":"#274878",color:on?"#C4B5FD":"#7BA5D8"}}>{c.name.replace("물류센터","")}</button>;
                                })}
                              </div>
                            </div>
                          )}
                          {!isGlobal&&<button onClick={()=>setEditingId(editingId===r.id?null:r.id)} style={{marginTop:8,background:"none",border:"none",color:"#7BA5D8",fontSize:11,cursor:"pointer",padding:0,fontFamily:"inherit"}}>{editingId===r.id?"▲ 접기":"✏️ 담당 센터 편집"}</button>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            {/* 오른쪽: 알림 이력 */}
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div style={{fontSize:15,fontWeight:700}}>🔔 알림 발송 이력</div>
                <div style={{fontSize:13,color:"#7BA5D8"}}>총 {alertLog.length}건</div>
              </div>
              {alertLog.length===0?(
                <div style={{background:"#162B50",border:"1px solid #274878",borderRadius:14,padding:48,textAlign:"center",color:"#7BA5D8"}}>
                  <div style={{fontSize:36,marginBottom:12}}>✅</div>
                  <div style={{fontSize:15,fontWeight:600,color:"#B8D0F0"}}>아직 발생한 알림이 없습니다</div>
                  <div style={{fontSize:13,marginTop:6}}>체감온도 33°C 이상 시 자동 발송됩니다</div>
                </div>
              ):alertLog.map(a=>{
                const lvl=getLvl(a.level);
                return (
                  <div key={a.id} className="alert-item" style={{borderColor:lvl.color}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span className="badge" style={{background:`${lvl.color}20`,color:lvl.color}}>{lvl.emoji} {a.level}</span>
                        <span style={{fontWeight:700}}>{a.center}</span>
                        <span style={{color:"#7BA5D8",fontSize:13}}>체감 {a.feelsLike}°C</span>
                      </div>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:3}}>
                        <div style={{fontSize:11,color:"#7BA5D8"}}>{a.time.toLocaleString("ko-KR")}</div>
                        <div style={{fontSize:11,color:"#4ADEAA"}}>📱 {a.recipientCount}명 수신</div>
                      </div>
                    </div>
                    <div style={{fontSize:12,color:"#7BA5D8",background:"#112240",padding:"8px 12px",borderRadius:6,fontFamily:"monospace",whiteSpace:"pre-wrap",border:"1px solid #274878"}}>{a.msg}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      <footer style={{borderTop:"1px solid #274878",padding:"16px 24px",display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12,color:"#3A6090",background:"#0F1F3D"}}>
        <div>기상청 초단기실황 API · 산업안전보건법 제24조 온열질환 예방 기준 · Platform WMS</div>
        <div>5분마다 자동 갱신 · 1시간 단위 평균 저장</div>
      </footer>
    </div>
  );
}